// packages
import { Injectable } from '@angular/core';
import { Observable, Subject, from } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
// services/custom classes
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { Node } from '@alfresco/js-api';
// interfaces
import { IServerSideGetRowsParams } from 'ag-grid-community';
import {
  IGridFilterModelType,
  IGridQueryParams,
  IGridResponseData,
  IGridResponseFiltersItems,
} from '../components/all-documents-dashboard/model';
// types
import { RequestParams } from '../types';
// constants
import { CONSTANTS } from '../model';

@Injectable({
  providedIn: 'root',
})
export class WKAllDocumentsDashboardService {
  public apiChanged = new Subject<void>();
  public readonly entriesMap = new Map<string, Node>();
  private readonly contentType = ['application/json'];
  private readonly dateFilterTypes = {
    equals: (date: IGridFilterModelType): string =>
      `[${date.dateFrom.split(' ')[0]}T00:00:00.000+00:00 TO ${
        date.dateFrom.split(' ')[0]
      }T23:59:59.999+00:00],`,
    inRange: (date: IGridFilterModelType): string =>
      `[${date.dateFrom.split(' ')[0]}T00:00:00.000+00:00 TO ${
        date.dateTo.split(' ')[0]
      }T23:59:59.999+00:00],`,
    greaterThan: (date: IGridFilterModelType): string => {
      const todayISOString = new Date().toISOString();
      const selectedDate = new Date(date.dateFrom.split(' ')[0]);
      selectedDate.setDate(selectedDate.getDate() + 1);
      const selectedDateISOString =
        this.getAdjustedDate(selectedDate).toISOString();
      return `[${selectedDateISOString} TO ${todayISOString}],`;
    },
    lessThan: (date: IGridFilterModelType): string => {
      const minValidDateISOString = new Date('2000-01-01').toISOString();
      const selectedDate = new Date(date.dateFrom.split(' ')[0]);
      const selectedDateISOString =
        this.getAdjustedDate(selectedDate).toISOString();
      return `[${minValidDateISOString} TO ${selectedDateISOString}],`;
    },
  };

  constructor(private readonly api: WkAlfrescoApiService) {}

  getAllDocumentsLibrary(
    queryParams: IGridQueryParams,
    params: IServerSideGetRowsParams
  ): Observable<IGridResponseData> {
    const payload: RequestParams = {
      path: `alfresco/s/wk/alldocsdashboard/data`,
      httpMethod: 'GET',
      contentTypes: this.contentType,
      accepts: this.contentType,
      headerParams: { 'Accept-Language': '' },
      queryParams: this.formatQueryParams(queryParams),
    };

    return from(this.api.callContentClientCustomApi(payload)).pipe(
      takeUntil(this.apiChanged.pipe(tap(() => params.fail())))
    );
  }

  getFilterValues(
    queryParams: IGridQueryParams
  ): Promise<IGridResponseFiltersItems[]> {
    const payload: RequestParams = {
      path: `alfresco/s/wk/alldocsdashboard/columnValues`,
      httpMethod: 'GET',
      contentTypes: this.contentType,
      accepts: this.contentType,
      headerParams: { 'Accept-Language': '' },
      queryParams: this.formatQueryParams(queryParams),
    };

    return this.api.callContentClientCustomApi(payload);
  }

  getAdjustedDate(date) {
    return new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  }

  replaceCommaAndEqualSign(value: string): string {
    return value.replace(/[=,]/g, ' ');
  }

  formatQueryParams(queryParams: IGridQueryParams): IGridQueryParams {
    const filtersFormatter = {
      text: (textValue: IGridFilterModelType, columName: string) => {
        // Here we replace occurrence of = and , to space. Reason is that these signs are part of special syntax for filtering which is used on backend side.
        // To prevent server errors during filtering by file name/title we decided to remove those signs before request made to backend
        queryParams._filter += `${columName}=*${this.replaceCommaAndEqualSign(
          textValue.filter
        )}*,`;
        return queryParams;
      },
      date: (dateValue: IGridFilterModelType, columName: string) => {
        queryParams._filter += `${columName}=${this.dateFilterTypes[
          dateValue.type
        ](dateValue)},`;
        return queryParams._filter;
      },
      set: (dataValues: IGridFilterModelType, columName: string) => {
        const concatFilters = dataValues.values;
        if (concatFilters.length > 0) {
          queryParams._filter += `${concatFilters
            .map(
              (val: string) =>
                `${columName}=${
                  val !== CONSTANTS.EMPTY_LABEL ? val : CONSTANTS.EMPTY
                }`
            )
            .join(',')},`;
          return queryParams._filter;
        }
        return '';
      },
    };

    if (queryParams._filterObject) {
      queryParams._filter = '';
      Object.keys(queryParams._filterObject).forEach((filterParam: string) => {
        filtersFormatter[queryParams._filterObject[filterParam].filterType](
          queryParams._filterObject[filterParam],
          filterParam
        );
      });
      delete queryParams._filterObject;
    }
    return queryParams;
  }
}
